import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Button, EmptyState, LoadingState } from '../../../../group_leader/components/common'
import { makeT } from '../../../../group_leader/helpers/i18n'
import {
  glApi,
  useListEventRosterQuery,
  useListGroupEventsQuery,
  useListGroupRosterQuery,
} from '../../../../group_leader/store/glApi'
import {
  addableRosterMembers, eventRosterRows, previousEventRows, SEARCH_DEBOUNCE_MS,
} from '../../../helpers/players'
import { formatHandicap } from '../../../../group_leader/helpers/player'
import GolferResultRow from '../../common/GolferResultRow'
import GolferSearchInput from '../../common/GolferSearchInput'
import EventPicker from '../../common/EventPicker'

const t = makeT('group_leader.event_setup.players.picker')

// The event roster's page cap; the group roster reports total_pages itself.
const EVENT_ROSTER_PAGE_SIZE = 100

// How close to the bottom of the scroller counts as "show me the rest".
const SCROLL_THRESHOLD_PX = 48

// Golfers to add from the group's roster or one of its past events; both feed the same bulk endpoint.
const SourcePicker = ({
  source, groupId, groupRosterLeagueId, currentLeagueId,
  enrolled, justAddedIds, onAdd, onAddAll, adding, addingAll,
}) => {
  const dispatch = useDispatch()
  const isEventSource = source === 'previous_event'

  const [ selectedEventId, setSelectedEventId ] = useState('')
  const [ page, setPage ] = useState(1)
  const [ extraRows, setExtraRows ] = useState([])
  const [ loadingMore, setLoadingMore ] = useState(false)
  // Roster only; a past event's field is short enough to scan.
  const [ query, setQuery ] = useState('')
  const [ debouncedQuery, setDebouncedQuery ] = useState('')
  const debounceRef = useRef(null)
  const enteredQueryRef = useRef(null)

  const { data: eventsData, isFetching: eventsLoading, isError: eventsError } = useListGroupEventsQuery(
    { groupId },
    { skip: !isEventSource || !groupId },
  )

  // Newest first; leagues#index orders by name.
  const events = useMemo(() => previousEventRows(eventsData?.data, currentLeagueId)
    .map((row) => ({
      id: String(row.id),
      name: row.attributes?.name || '',
      startDate: row.attributes?.start_date || null,
    }))
    .sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || ''))),
  [ eventsData, currentLeagueId ])

  const rosterQuery = useListGroupRosterQuery(
    { page: 1, filterLetters: debouncedQuery }, { skip: isEventSource },
  )
  const eventRosterQuery = useListEventRosterQuery(
    { leagueId: selectedEventId, page: 1 },
    { skip: !isEventSource || !selectedEventId },
  )

  const active = isEventSource ? eventRosterQuery : rosterQuery
  const firstPageRows = isEventSource
    ? eventRosterRows(eventRosterQuery.data?.members)
    : (rosterQuery.data?.members || [])

  const totalPages = isEventSource
    ? Math.max(1, Math.ceil((eventRosterQuery.data?.total_members || 0) / EVENT_ROSTER_PAGE_SIZE))
    : (rosterQuery.data?.total_pages || 1)

  const loaded = [ ...firstPageRows, ...extraRows ]

  // `justAddedIds` covers the gap until the tee sheet refetch, so a row cannot be added twice.
  const addable = useMemo(
    () => addableRosterMembers(loaded, enrolled).filter((member) => !justAddedIds.has(member.memberId)),
    [ loaded, enrolled, justAddedIds ],
  )

  // The roster's JSON is already display-formatted; v3's event roster sends the raw column.
  const indexOf = (member) => (
    isEventSource ? formatHandicap(member.usgaIndex) : (member.usgaIndex || null)
  )

  // A fixed-height scroller: scrolling reveals the rest, with no "show more" button.
  const hasMoreOnServer = page < totalPages

  // Later pages are fetched imperatively, so "add all" can mean every page.
  const fetchPage = async (pageNumber) => {
    if (isEventSource) {
      const result = await dispatch(
        glApi.endpoints.listEventRoster.initiate({ leagueId: selectedEventId, page: pageNumber }),
      )
      return eventRosterRows(result.data?.members)
    }
    const result = await dispatch(
      glApi.endpoints.listGroupRoster.initiate({ page: pageNumber, filterLetters: debouncedQuery }),
    )
    return result.data?.members || []
  }

  // Sequential on purpose: TMS API throttles by path and answers a burst with 429.
  const loadRemainingPages = async () => {
    if (page >= totalPages) { return [] }
    setLoadingMore(true)
    const rest = []
    for (let next = page + 1; next <= totalPages; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      rest.push(...await fetchPage(next))
    }
    setExtraRows((prev) => [ ...prev, ...rest ])
    setPage(totalPages)
    setLoadingMore(false)
    return rest
  }

  const resetList = () => {
    setPage(1)
    setExtraRows([])
    setQuery('')
  }

  const handleEventSelect = (eventId) => {
    setSelectedEventId(eventId)
    resetList()
  }

  // members.json filters server-side; the paging resets since the filtered set is a new list.
  const handleQueryChange = (value) => {
    setQuery(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value)
      setExtraRows([])
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
  }

  // Enter adds the first golfer, as the search panel does, once no page is loading and Add all is done;
  // a repeated Enter on the same text while its add runs is ignored.
  const handleQueryEnter = () => {
    if (addingAll || loadingMore || !query.trim()) { return }
    if (adding && enteredQueryRef.current === query) { return }
    enteredQueryRef.current = query
    const [ candidate ] = addable
    if (candidate) { onAdd(candidate) }
  }

  // Reaching the end of the scroller pulls in the next page, not the whole roster on open.
  const handleScroll = async (event) => {
    if (!hasMoreOnServer || loadingMore) { return }
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD_PX) { return }
    setLoadingMore(true)
    const next = page + 1
    const rows = await fetchPage(next)
    setExtraRows((prev) => [ ...prev, ...rows ])
    setPage(next)
    setLoadingMore(false)
  }

  // Every golfer the source has: the remaining pages are pulled in first.
  const handleAddAll = async () => {
    const rest = await loadRemainingPages()
    const ids = addableRosterMembers([ ...loaded, ...rest ], enrolled)
      .filter((member) => !justAddedIds.has(member.memberId))
      .map((member) => member.memberId)
    if (ids.length > 0) {
      onAddAll(isEventSource ? selectedEventId : groupRosterLeagueId, ids)
    }
  }

  if (isEventSource && eventsLoading) { return <LoadingState label={t('loading_events')}/> }
  if (isEventSource && eventsError) { return <EmptyState title={t('events_error')}/> }
  if (isEventSource && events.length === 0) {
    return <EmptyState title={t('no_events')} subtitle={t('no_events_hint')}/>
  }

  const listReady = isEventSource ? Boolean(selectedEventId) : true
  const listLoading = active.isFetching && loaded.length === 0

  return (
    <div className="gl-event-setup-source-panel">
      <div className="gl-event-setup-source-panel-hd">
        <span className="gl-event-setup-source-panel-title">
          {isEventSource ? t('previous_event_title') : t('roster_title')}
        </span>
      </div>

      <div className="gl-event-setup-source-panel-controls">
        {isEventSource && (
          <div className="gl-event-setup-source-panel-field">
            <span className="gl-event-setup-source-panel-label">{t('event_label')}</span>
            {/* Name only: a date doubled the row length on a phone. */}
            <EventPicker events={events} selectedId={selectedEventId} onSelect={handleEventSelect}/>
          </div>
        )}

        {!isEventSource && listReady && !listLoading && (
          <div className="gl-event-setup-source-panel-field">
            <label className="gl-event-setup-source-panel-label" htmlFor="gl-picker-search">{t('search_label')}</label>
            <GolferSearchInput
              id="gl-picker-search"
              value={query}
              onChange={handleQueryChange}
              onEnter={handleQueryEnter}
              placeholder={t('search_placeholder')}
              ariaLabel={t('search_aria')}
            />
          </div>
        )}

        {listReady && !listLoading && addable.length > 0 && (
          <div className="gl-event-setup-source-panel-action">
            {loadingMore && <span className="gl-event-setup-source-panel-loading">{t('loading_more')}</span>}
            <Button variant="primary" size="sm" disabled={addingAll || loadingMore} onClick={handleAddAll}>
              {addingAll ? t('adding_all') : t('add_all')}
            </Button>
          </div>
        )}
      </div>

      {listReady && listLoading && <LoadingState label={t('loading_golfers')}/>}
      {listReady && active.isError && <EmptyState title={t('roster_error')}/>}

      {listReady && !listLoading && !active.isError && addable.length === 0 && (
        query.trim()
          ? <EmptyState title={t('no_matches', { query: query.trim() })}/>
          : (
            <EmptyState
              title={isEventSource ? t('all_added_event') : t('all_added_roster')}
              subtitle={t('all_added_hint')}
            />
          )
      )}

      {listReady && !listLoading && addable.length > 0 && (
        // The same row as the search results, whichever source offered the golfer.
        <div className="gl-event-setup-search-results" onScroll={handleScroll}>
          {addable.map((member) => (
            <GolferResultRow
              key={member.memberId}
              name={member.name}
              handicapDisplay={indexOf(member)}
            >
              <Button variant="primary" size="sm" disabled={adding} onClick={() => onAdd(member)}>
                {t('add')}
              </Button>
            </GolferResultRow>
          ))}
        </div>
      )}
    </div>
  )
}

SourcePicker.propTypes = {
  source: PropTypes.oneOf([ 'roster', 'previous_event' ]).isRequired,
  groupId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  groupRosterLeagueId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  currentLeagueId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  enrolled: PropTypes.array.isRequired,
  justAddedIds: PropTypes.instanceOf(Set).isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddAll: PropTypes.func.isRequired,
  adding: PropTypes.bool,
  addingAll: PropTypes.bool,
}

export default SourcePicker
